// Firebase modular SDK initialization. Web config is public-by-design — these
// values are not secrets. Real security lives in Firestore/Storage rules and
// in custom-claims-aware Cloud Functions.
//
// Replace this config with the actual `sevric-portal` project values from
// Firebase console → Project settings → Your apps → web app config.
import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';

// Firebase web SDK config values are PUBLIC by design — they're embedded in
// every browser bundle anyway. Real security lives in Firestore rules + custom
// claims, not in keeping these secret. We REQUIRE every value via env so that
// a misconfigured staging build can't silently point at the prod project.
// Source of truth for the values: portal/public/js/firebase-config.js
// — keep the two files in lockstep.
function required(name) {
  const v = import.meta.env[name];
  if (!v || /^REPLACE_ME/.test(v)) {
    throw new Error(
      `Missing Firebase config: ${name}. Add it to recon-app-v2/.env and rebuild.`
    );
  }
  return v;
}

const firebaseConfig = {
  apiKey: required('VITE_FIREBASE_API_KEY'),
  authDomain: required('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: required('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: required('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: required('VITE_FIREBASE_SENDER_ID'),
  appId: required('VITE_FIREBASE_APP_ID'),
};

export const app = initializeApp(firebaseConfig);

// App Check — reCAPTCHA Enterprise. The site key is public-by-design (it's
// embedded in the client bundle and shipped to every browser). Real
// protection comes from the matching `APP_CHECK_ENFORCE=true` env on the
// functions side, which rejects any callable invocation without a valid token.
// If VITE_APP_CHECK_SITE_KEY is missing we skip init — useful for local dev.
const APP_CHECK_SITE_KEY = import.meta.env.VITE_APP_CHECK_SITE_KEY;
if (APP_CHECK_SITE_KEY && !/^REPLACE_ME/.test(APP_CHECK_SITE_KEY)) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    if (import.meta.env.PROD) console.warn('App Check init failed:', e);
  }
}

// SEVRIC Chat needs the FirebaseApp instance to mint the Supabase JWT.
export function getFirebaseApp() { return app; }

export const auth = getAuth(app);
export const db = getFirestore(app);
// Cloud Functions live in us-central1 (matches portal/functions deploy region).
export const functions = getFunctions(app, 'us-central1');

// Local persistence so the user stays signed in across reloads. Per-origin —
// portal.sevric.com and recon.sevric.com each maintain their own auth state.
//
// If this fails (Safari private mode, blocked storage, sandboxed iframe, etc.)
// we silently fall back to Firebase's default in-memory persistence, which
// signs the user out on every reload. Surface that explicitly so the bug is
// diagnosable instead of a "why am I logged out?" mystery.
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.warn(
    '[recon] browserLocalPersistence unavailable — auth will not survive reloads.',
    err?.message || err,
  );
});
